import { HostAriaLabelMixin, SchmancyElement } from '@schmancy/mixins/index'
import { css, html, LitElement, type PropertyValues } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { when } from 'lit/directives/when.js'
import { magnetic } from '@schmancy/directives/magnetic'
import type { ControlSize } from '@schmancy/theme/control-size'
import { FocusHandoff } from '@schmancy/utils/focus-handoff'
import { ButtonColor, ButtonVariant } from './button'
import '../icons/icon'
import '../busy/spinner'

/**
 * The spinner rung per size — the same px the glyph ladder above renders, so
 * the spinner that replaces the icon while busy occupies the icon's own box and
 * the control's geometry does not move. 'xl' wants 36px, which the spinner's
 * token ladder does not carry, so it takes the numeric form (4px units).
 */
const BUSY_SPINNER_SIZE: Record<'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl', 'xxs' | 'xs' | 'sm' | 'md' | number> = {
	xxs: 'xxs',
	xs: 'xs',
	sm: 'sm',
	md: 'md',
	lg: 'md',
	xl: 9,
}

// Tailwind v4's JIT scanner only extracts class names from static strings — a template
// literal like `h-control-${size}` never generates a utility. Every name is spelled out.
const ICON_BTN_SQUARE_CLASS: Record<ControlSize, string> = {
	xxs: 'h-control-xxs w-control-xxs',
	xs: 'h-control-xs w-control-xs',
	sm: 'h-control-sm w-control-sm',
	md: 'h-control-md w-control-md',
	lg: 'h-control-lg w-control-lg',
}

// text-[var(--icon-button-*)] emits `color` in Tailwind v4 (custom-property values have no
// inferred type) — `text-[length:var(--icon-button-*)]` is required for `font-size`.
const ICON_BTN_GLYPH_CLASS: Record<ControlSize, string> = {
	xxs: 'text-[length:var(--icon-button-xxs)]',
	xs: 'text-[length:var(--icon-button-xs)]',
	sm: 'text-[length:var(--icon-button-sm)]',
	md: 'text-[length:var(--icon-button-md)]',
	lg: 'text-[length:var(--icon-button-lg)]',
}

/**
 * Every `ButtonVariant`, plus the one job only an icon button has: sitting on
 * content whose colour cannot be known — a photo, a video frame, a map — and
 * staying legible on it.
 *
 * No theme-coloured variant can do that, because the theme's roles are chosen
 * against the theme's own surfaces and nothing says the picture underneath is
 * one; and the two containerless variants (`text`, `plain`) vanish outright the
 * moment a light photo scrolls under them. A scrim carries its own contrast
 * with it: a neutral dark disc under a light glyph, legible over a white sky
 * and a black shadow alike. Its `color` is ignored — neutral by construction is
 * the whole of why it survives a background it cannot see.
 *
 * The disc's 80% clears AAA, not just the AA floor. Being translucent, whatever
 * is behind it counts in the glyph's contrast, and the worst ground is white:
 * the white glyph measures 2.85:1 at 40%, 4.76:1 at 55% (AA's 53.5% floor), and
 * 12.63:1 at 80% (past AAA's 7:1). 55% already cleared AA on paper, but a small
 * icon read live over an actual photo still under-read at that floor — AAA
 * headroom is what a chrome control needs when the ground behind it is a real
 * image, not a flat swatch. The cost is the dark case's own edge losing nothing
 * (19.6 -> 19.9:1) while the disc's transmittance drops 45% -> 20%.
 *
 * The disc is inset inside the control's own box, so the target the finger has
 * to hit stays the full `size` while the shape the eye reads is smaller — the
 * one place a chrome control is laid over someone's picture is the place to
 * spend less of it.
 */
export type IconButtonVariant = ButtonVariant | 'scrim'

/** @csspart base - The underlying native `<button>` (or `<a>` when `href` is set). */
@customElement('schmancy-icon-button')
export class SchmnacyIconButton extends HostAriaLabelMixin(SchmancyElement) {
	static properties = {
		title: { noAccessor: true },
	}

	static styles = [
		css`
			:host {
				display: inline-flex;
				flex-shrink: 0;
				border-radius: var(
					--schmancy-radius-full,
					calc(infinity * 1px)
				); /* mirrors rounded-full on [part=base] below */
				transition:
					box-shadow 300ms cubic-bezier(0.34, 1.56, 0.64, 1),
					transform 200ms cubic-bezier(0.34, 1.56, 0.64, 1);
				/* Set to 1 by an overlay a MODAL overlay has covered, which is the only
				   case the sweep inerts — an anchored menu leaves its opener live. A
				   button's whole content is an action, so once inert it says nothing:
				   it cannot be pressed and it reports no value. That is what separates
				   it from a switch or a field, which still show their value covered and
				   so keep painting — the page behind a viewer stays readable, it just
				   stops offering what it cannot do. Opacity rather than display, so the
				   covered surface keeps its layout and nothing reflows under the reader
				   when the overlay above opens or closes. Unset reads 0. */
				opacity: calc(1 - var(--schmancy-overlay-covered, 0));
			}
			:host([width='full']) {
				display: flex;
				width: 100%;
			}
			:host(:state(busy)) {
				/* The same treatment schmancy-button gives a busy action, reached the
				   same way — dimmed to the disabled opacity token while staying
				   focusable, because a disabled control drops from the tab order and a
				   keyboard user would lose their place mid-operation (WCAG 2.2 AA).

				   Multiplied by the covered factor from :host above, which this rule
				   would otherwise outrank and hand a covered surface a visible button
				   back for the one case — mid-flight when the overlay above opened —
				   where it is least able to finish. Uncovered the factor is 1 and the
				   dim is exactly the token. */
				opacity: calc(var(--schmancy-sys-state-opacity-disabled, 0.38) * (1 - var(--schmancy-overlay-covered, 0)));
				cursor: progress;
			}
			:host(:hover:not([disabled]):not([readonly]):not(:state(busy))) {
				box-shadow: 0 2px 12px -4px color-mix(in srgb, var(--schmancy-sys-color-primary-default) 18%, transparent);
			}
			:host(:active:not([disabled]):not([readonly]):not(:state(busy))) {
				transform: scale(0.92);
				box-shadow: none;
				transition-duration: 100ms;
			}
			@media (prefers-reduced-motion: reduce) {
				:host {
					transition: none;
				}
				:host(:hover:not([disabled])) {
					box-shadow: none;
				}
				:host(:active:not([disabled])) {
					transform: none;
					box-shadow: none;
				}
			}
		`,
	]

	protected static shadowRootOptions = {
		...LitElement.shadowRootOptions,
		mode: 'open',
		delegatesFocus: true,
	}

	/** Only for `states` — this control is not form-associated, so there is no `internals.form` to read. */
	private internals: ElementInternals | undefined

	constructor() {
		super()
		try {
			this.internals = this.attachInternals()
		} catch {
			this.internals = undefined
		}
		// Constructor-registered, so it precedes every consumer's own `@click`
		// binding on this host and `stopImmediatePropagation` reaches them. This
		// is the half of `busy` that prevents the second press — the motivating
		// case being an icon button that starts a slow regeneration and, with no
		// in-flight state at all, invites a second one.
		this.addEventListener('click', (e: MouseEvent) => {
			if (!this.busy && !this.readonly) return
			e.preventDefault()
			e.stopImmediatePropagation()
		})
	}

	@query('[part="base"]', true)
	private nativeElement!: HTMLElement

	@property({ type: String })
	public size: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' = 'md'

	@property({ reflect: true, type: String })
	public variant: IconButtonVariant = 'text'

	/** Falls back to a variant-derived default when unset: 'secondary' for tonal, else 'primary' — same rule as `schmancy-button`. Ignored by `scrim`, which is neutral by construction. */
	@property({ reflect: true, type: String })
	public color?: ButtonColor

	@property({ reflect: true })
	public width: 'full' | 'auto' = 'auto'

	@property({ reflect: true, type: String })
	public type: 'button' | 'reset' | 'submit' = 'button'

	/** Renders as an anchor when set. */
	@property()
	public href?: string

	@property({ type: Boolean, reflect: true })
	public disabled = false

	/**
	 * The button's own action is in flight. Same contract as `schmancy-button`'s
	 * `busy`, and deliberately not `disabled`: `aria-disabled` says "you cannot",
	 * `aria-busy` says "wait", and only the first drops the control out of the
	 * tab order. The glyph is replaced by a spinner at the glyph's own size — an
	 * icon button's whole content is that one glyph, so there is nowhere else for
	 * an in-flight signal to live and no geometry to shift while it does.
	 */
	@property({ type: Boolean, reflect: true })
	public busy = false

	/** Same contract as `schmancy-button`'s `readonly`: a read-only field's trigger, focusable, named and at full ink, offering no act. */
	@property({ type: Boolean, reflect: true })
	public readonly = false

	/** Render slot content as text instead of wrapping in schmancy-icon. */
	@property({ type: Boolean, reflect: true })
	public text = false

	/**
	 * Opts out of the hover-follow pull the `magnetic` directive applies by
	 * default. A modal's sole dismiss control (a lightbox close button, an
	 * overlay's escape hatch) must present a hit target that never moves under
	 * the cursor mid-gesture — set `false` there. Every other consumer is
	 * unaffected: default `true` is pixel/behavior-identical to before this
	 * property existed.
	 */
	@property({ type: Boolean })
	public magnetic = true

	/** Same contract as `schmancy-button`'s `focus-successor`: where focus goes when this button removes itself. */
	@property({ attribute: 'focus-successor' })
	public focusSuccessor?: string

	protected readonly focusHandoff = new FocusHandoff(this, () => this.focusSuccessor)

	/** Use instead of slot content to prevent translation from breaking icon lookup. */
	@property({ type: String })
	public icon?: string

	// Mirrors <schmancy-icon>: a MutationObserver on the light DOM captures the icon name
	// no matter how it's passed — plain text, a dynamic ${...} binding (Lit mutates the text
	// node's data in place, so no slotchange fires), or a nested <schmancy-icon> element.
	@state()
	private capturedIcon?: string

	private observer?: MutationObserver

	override attributeChangedCallback(name: string, oldValue: string | null, value: string | null): void {
		if (name === 'title') {
			// The native reflector owns title, including attribute removal.
			if (oldValue !== value) this.requestUpdate()
			return
		}
		super.attributeChangedCallback(name, oldValue, value)
	}

	connectedCallback(): void {
		super.connectedCallback()
		this.captureIcon()
		this.observer = new MutationObserver(() => this.captureIcon())
		this.observer.observe(this, { childList: true, characterData: true, subtree: true })
	}

	disconnectedCallback(): void {
		super.disconnectedCallback()
		this.observer?.disconnect()
	}

	private captureIcon(): void {
		if (this.icon || this.text) return
		this.capturedIcon = this.textContent?.trim() || undefined
	}

	/**
	 * The one writer of the busy treatment. `aria-busy` is mirrored onto the host
	 * and, in render(), onto `[part="base"]` — the node that actually carries the
	 * button role, for the same reason the name and the other ARIA state move
	 * there. `:state(busy)` is the CSS hook, named identically on both buttons so
	 * one rule reaches either control.
	 */
	protected override willUpdate(changed: PropertyValues): void {
		super.willUpdate(changed)
		if (!changed.has('busy')) return
		if (this.busy) {
			this.setAttribute('aria-busy', 'true')
			this.internals?.states.add('busy')
		} else {
			this.removeAttribute('aria-busy')
			this.internals?.states.delete('busy')
		}
	}

	/**
	 * Same reasoning as the name above, for the ARIA state a trigger sets: the
	 * host is a roleless generic node, so a popup relationship, an open state or
	 * an invalid marking written there reaches no assistive technology until it
	 * lands on `[part="base"]`. Reactive properties rather than a render-time host
	 * read, because a consumer's attribute binding schedules no update here.
	 * Unset on every existing consumer, which renders exactly as before.
	 */
	@property({ attribute: 'aria-haspopup' })
	public override ariaHasPopup: 'true' | 'false' | 'menu' | 'listbox' | 'tree' | 'grid' | 'dialog' | null = null

	@property({ attribute: 'aria-pressed' })
	public override ariaPressed: 'true' | 'false' | 'mixed' | null = null

	@property({ attribute: 'aria-expanded' })
	public override ariaExpanded: 'true' | 'false' | null = null

	@property({ attribute: 'aria-invalid' })
	public override ariaInvalid: 'true' | 'false' | 'grammar' | 'spelling' | null = null

	public override focus(options?: FocusOptions) {
		this.nativeElement.focus(options)
	}

	public override blur() {
		this.nativeElement.blur()
	}

	click(): void {
		this.dispatchEvent(new Event('click', { bubbles: true, composed: true }))
	}

	private preventDefault(event: Event) {
		event.preventDefault()
		event.stopPropagation()
	}

	protected firstUpdated(): void {}

	/** Same fallback rule as `schmancy-button`: explicit `color` wins; otherwise 'secondary' for tonal, else 'primary'. */
	protected get effectiveColor(): ButtonColor {
		if (this.color) return this.color
		const effectiveVariant = this.variant === 'tonal' ? 'filled tonal' : this.variant
		return effectiveVariant === 'filled tonal' ? 'secondary' : 'primary'
	}

	render() {
		const effectiveVariant = this.variant === 'tonal' ? 'filled tonal' : this.variant
		const effectiveColor = this.effectiveColor

		// xxs..lg are on the shared control ladder (height/width via --spacing-control-<size>,
		// glyph via --icon-button-<size>); xl is a component-local extension with no shared token.
		const SHARED_SIZES: ReadonlyArray<ControlSize> = ['xxs', 'xs', 'sm', 'md', 'lg']
		const isShared = SHARED_SIZES.includes(this.size as ControlSize)

		const classes = {
			'z-0 transition-all duration-200 relative rounded-full inline-flex justify-center items-center gap-[8px] outline-secondary-default focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2 outline-hidden': true,
			[isShared ? ICON_BTN_SQUARE_CLASS[this.size as ControlSize] : '']: isShared,
			'p-5': this.size === 'xl', // 20px padding + 40px icon = 80px total
			'opacity-[var(--schmancy-sys-state-opacity-disabled,0.38)]': this.disabled,
			'cursor-pointer': !this.disabled && !this.busy && !this.readonly,
			'cursor-default': this.readonly && !this.disabled && !this.busy,
			// The :host rule's `cursor: progress` cannot reach the pointer on its
			// own — the pointer is over THIS element across the whole box and its
			// own `cursor-pointer` wins, so progress is declared here too.
			'cursor-progress': !this.disabled && this.busy,
			'hover:shadow-xs':
				!this.disabled &&
				!this.busy &&
				!this.readonly &&
				(effectiveVariant === 'outlined' ||
					effectiveVariant === 'text' ||
					effectiveVariant === 'plain' ||
					effectiveVariant === 'filled' ||
					effectiveVariant === 'filled tonal'),
			'hover:shadow-sm': !this.disabled && !this.busy && !this.readonly && effectiveVariant === 'elevated',
			'flex-1 text-center': this.width === 'full',
			'bg-surface-low shadow-xs': effectiveVariant === 'elevated',
			'bg-transparent border-1 border-solid': effectiveVariant === 'outlined',
			// Neutral outlined controls share the resting frame role with form fields.
			'border-control-border':
				effectiveVariant === 'outlined' && (effectiveColor === 'primary' || effectiveColor === 'neutral'),
			'border-secondary-default': effectiveVariant === 'outlined' && effectiveColor === 'secondary',
			'border-success-default': effectiveVariant === 'outlined' && effectiveColor === 'success',
			'border-error-default': effectiveVariant === 'outlined' && effectiveColor === 'error',
			'border-warning-default': effectiveVariant === 'outlined' && effectiveColor === 'warning',
			'border-info-default': effectiveVariant === 'outlined' && effectiveColor === 'info',
			'bg-primary-default text-primary-on': effectiveVariant === 'filled' && effectiveColor === 'primary',
			'bg-secondary-default text-secondary-on': effectiveVariant === 'filled' && effectiveColor === 'secondary',
			// The state roles fill from `pinned` — the scheme-stable pair whose
			// measurements live on `errorPinned` in theme.format.ts — and ring the
			// boundary that ground owes from the role's own `default`.
			'bg-success-pinned text-success-on-pinned ring-success-default':
				effectiveVariant === 'filled' && effectiveColor === 'success',
			'bg-error-pinned text-error-on-pinned ring-error-default':
				effectiveVariant === 'filled' && effectiveColor === 'error',
			'bg-warning-pinned text-warning-on-pinned ring-warning-default':
				effectiveVariant === 'filled' && effectiveColor === 'warning',
			'bg-info-pinned text-info-on-pinned ring-info-default':
				effectiveVariant === 'filled' && effectiveColor === 'info',
			// One key for the width, because classMap splits a multi-token key into
			// single-token entries and a second key repeating `ring-1` would overwrite
			// the first for whichever role rendered last.
			'ring-1 ring-inset':
				effectiveVariant === 'filled' &&
				(effectiveColor === 'success' ||
					effectiveColor === 'error' ||
					effectiveColor === 'warning' ||
					effectiveColor === 'info'),
			'bg-surface-containerHighest text-surface-on': effectiveVariant === 'filled' && effectiveColor === 'neutral',
			// The disc itself is a separate inset layer below, so the target keeps
			// the full control size while the shape reads smaller. It sits at
			// `-z-10` because a positioned layer at `z-0` paints over the in-flow
			// glyph; this button's `relative z-0` keeps it above the ground. Only the ink
			// belongs on the button: white in both schemes, because the scrim is
			// tone 0 in both and its ink cannot follow a scheme its ground does not.
			'text-white': effectiveVariant === 'scrim',
			'bg-primary-container text-primary-onContainer':
				effectiveVariant === 'filled tonal' && effectiveColor === 'primary',
			'bg-secondary-container text-secondary-onContainer':
				effectiveVariant === 'filled tonal' && effectiveColor === 'secondary',
			'bg-success-container text-success-onContainer':
				effectiveVariant === 'filled tonal' && effectiveColor === 'success',
			'bg-error-container text-error-onContainer': effectiveVariant === 'filled tonal' && effectiveColor === 'error',
			'bg-warning-container text-warning-onContainer':
				effectiveVariant === 'filled tonal' && effectiveColor === 'warning',
			'bg-info-container text-info-onContainer': effectiveVariant === 'filled tonal' && effectiveColor === 'info',
			'bg-surface-containerLow text-surface-onVariant':
				effectiveVariant === 'filled tonal' && effectiveColor === 'neutral',
			'text-primary-default':
				(effectiveVariant === 'text' ||
					effectiveVariant === 'plain' ||
					effectiveVariant === 'elevated' ||
					effectiveVariant === 'outlined') &&
				effectiveColor === 'primary',
			'text-secondary-default':
				(effectiveVariant === 'text' ||
					effectiveVariant === 'plain' ||
					effectiveVariant === 'elevated' ||
					effectiveVariant === 'outlined') &&
				effectiveColor === 'secondary',
			'text-success-default':
				(effectiveVariant === 'text' ||
					effectiveVariant === 'plain' ||
					effectiveVariant === 'elevated' ||
					effectiveVariant === 'outlined') &&
				effectiveColor === 'success',
			'text-error-default':
				(effectiveVariant === 'text' ||
					effectiveVariant === 'plain' ||
					effectiveVariant === 'elevated' ||
					effectiveVariant === 'outlined') &&
				effectiveColor === 'error',
			'text-warning-default':
				(effectiveVariant === 'text' ||
					effectiveVariant === 'plain' ||
					effectiveVariant === 'elevated' ||
					effectiveVariant === 'outlined') &&
				effectiveColor === 'warning',
			'text-info-default':
				(effectiveVariant === 'text' ||
					effectiveVariant === 'plain' ||
					effectiveVariant === 'elevated' ||
					effectiveVariant === 'outlined') &&
				effectiveColor === 'info',
			'text-surface-onVariant':
				(effectiveVariant === 'text' ||
					effectiveVariant === 'plain' ||
					effectiveVariant === 'elevated' ||
					effectiveVariant === 'outlined') &&
				effectiveColor === 'neutral',
		}

		const stateLayerClasses = {
			'absolute hover:opacity-[var(--schmancy-sys-state-opacity-hover,0.08)] rounded-full z-0': true,
			// Over the shape the eye reads, never over the dead ring of target
			// around a scrim's disc — a wash wider than its own disc reads as a
			// second, larger control appearing on hover.
			'inset-0': effectiveVariant !== 'scrim',
			'inset-1': effectiveVariant === 'scrim',
			'hover:bg-primary-on': effectiveVariant === 'filled' && effectiveColor === 'primary',
			'hover:bg-secondary-on': effectiveVariant === 'filled' && effectiveColor === 'secondary',
			// The wash rides the ink that sits ON the fill, so it follows the fill to
			// `pinned`: `<role>-on` is a dark tone in dark scheme and would wash a
			// dark ground with dark, collapsing hover/focus/pressed to 1.05:1.
			'hover:bg-success-on-pinned': effectiveVariant === 'filled' && effectiveColor === 'success',
			'hover:bg-error-on-pinned': effectiveVariant === 'filled' && effectiveColor === 'error',
			'hover:bg-warning-on-pinned': effectiveVariant === 'filled' && effectiveColor === 'warning',
			'hover:bg-info-on-pinned': effectiveVariant === 'filled' && effectiveColor === 'info',
			'hover:bg-surface-on': effectiveVariant === 'filled' && effectiveColor === 'neutral',
			'hover:bg-white': effectiveVariant === 'scrim',
			'hover:bg-primary-default':
				(effectiveVariant === 'outlined' ||
					effectiveVariant === 'elevated' ||
					effectiveVariant === 'text' ||
					effectiveVariant === 'plain') &&
				effectiveColor === 'primary',
			'hover:bg-secondary-default':
				(effectiveVariant === 'outlined' ||
					effectiveVariant === 'elevated' ||
					effectiveVariant === 'text' ||
					effectiveVariant === 'plain') &&
				effectiveColor === 'secondary',
			'hover:bg-success-default':
				(effectiveVariant === 'outlined' ||
					effectiveVariant === 'elevated' ||
					effectiveVariant === 'text' ||
					effectiveVariant === 'plain') &&
				effectiveColor === 'success',
			'hover:bg-error-default':
				(effectiveVariant === 'outlined' ||
					effectiveVariant === 'elevated' ||
					effectiveVariant === 'text' ||
					effectiveVariant === 'plain') &&
				effectiveColor === 'error',
			'hover:bg-warning-default':
				(effectiveVariant === 'outlined' ||
					effectiveVariant === 'elevated' ||
					effectiveVariant === 'text' ||
					effectiveVariant === 'plain') &&
				effectiveColor === 'warning',
			'hover:bg-info-default':
				(effectiveVariant === 'outlined' ||
					effectiveVariant === 'elevated' ||
					effectiveVariant === 'text' ||
					effectiveVariant === 'plain') &&
				effectiveColor === 'info',
			'hover:bg-surface-onVariant':
				(effectiveVariant === 'outlined' ||
					effectiveVariant === 'elevated' ||
					effectiveVariant === 'text' ||
					effectiveVariant === 'plain') &&
				effectiveColor === 'neutral',
			'hover:bg-primary-container': effectiveVariant === 'filled tonal' && effectiveColor === 'primary',
			'hover:bg-secondary-container': effectiveVariant === 'filled tonal' && effectiveColor === 'secondary',
			'hover:bg-success-container': effectiveVariant === 'filled tonal' && effectiveColor === 'success',
			'hover:bg-error-container': effectiveVariant === 'filled tonal' && effectiveColor === 'error',
			'hover:bg-warning-container': effectiveVariant === 'filled tonal' && effectiveColor === 'warning',
			'hover:bg-info-container': effectiveVariant === 'filled tonal' && effectiveColor === 'info',
			'hover:bg-surface-containerLow': effectiveVariant === 'filled tonal' && effectiveColor === 'neutral',
		}

		const iconSizeClass = isShared ? ICON_BTN_GLYPH_CLASS[this.size as ControlSize] : 'text-4xl' // xl: 36px icon

		const effectiveAriaLabel =
			this.ariaLabel || (this.text ? undefined : this.title.trim() || this.icon || this.capturedIcon || undefined)

		if (this.href) {
			return html`
				<a
					${magnetic({ strength: this.magnetic && !this.disabled && !this.busy && !this.readonly ? 3 : 0, radius: 50 })}
					part="base"
					href=${ifDefined(this.disabled || this.readonly || this.busy ? undefined : this.href)}
					role=${ifDefined(!this.disabled && (this.readonly || this.busy) ? 'link' : undefined)}
					aria-label=${ifDefined(effectiveAriaLabel)}
					aria-pressed=${ifDefined(this.ariaPressed ?? undefined)}
					aria-haspopup=${ifDefined(this.readonly ? undefined : (this.ariaHasPopup ?? undefined))}
					aria-expanded=${ifDefined(this.readonly ? undefined : (this.ariaExpanded ?? undefined))}
					aria-invalid=${ifDefined(this.ariaInvalid ?? undefined)}
					aria-busy=${ifDefined(this.busy ? 'true' : undefined)}
					class="${this.classMap(classes)}"
					tabindex=${this.disabled ? '-1' : '0'}
					aria-disabled=${this.disabled}
					@click=${this.disabled || this.busy ? this.preventDefault : undefined}
				>
					${when(effectiveVariant === 'scrim', () => html`<div class="absolute inset-1 rounded-full bg-scrim/80 pointer-events-none -z-10"></div>`)}
					${when(!this.disabled && !this.busy && !this.readonly, () => html`<div class="${this.classMap(stateLayerClasses)}"></div>`)}
					${when(this.busy, () => html`<schmancy-spinner aria-hidden="true" .size=${BUSY_SPINNER_SIZE[this.size]}></schmancy-spinner>`)}
					${
						this.text
							? html`<slot></slot>`
							: html`
									${when(
										!this.busy,
										() => html`<schmancy-icon class=${iconSizeClass}>${this.icon || this.capturedIcon}</schmancy-icon>`,
									)}
								`
					}
				</a>
			`
		}

		return html`
			<button
				${magnetic({ strength: this.magnetic && !this.disabled && !this.busy && !this.readonly ? 3 : 0, radius: 50 })}
				part="base"
				aria-label=${ifDefined(effectiveAriaLabel)}
					aria-pressed=${ifDefined(this.ariaPressed ?? undefined)}
				aria-haspopup=${ifDefined(this.readonly ? undefined : (this.ariaHasPopup ?? undefined))}
				aria-expanded=${ifDefined(this.readonly ? undefined : (this.ariaExpanded ?? undefined))}
				aria-invalid=${ifDefined(this.ariaInvalid ?? undefined)}
				aria-busy=${ifDefined(this.busy ? 'true' : undefined)}
				?disabled=${this.disabled}
				class="${this.classMap(classes)}"
				type=${ifDefined(this.type)}
				tabindex=${ifDefined(this.disabled ? '-1' : undefined)}
			>
				${when(effectiveVariant === 'scrim', () => html`<div class="absolute inset-1 rounded-full bg-scrim/80 pointer-events-none -z-10"></div>`)}
				${when(!this.disabled && !this.busy && !this.readonly, () => html`<div class="${this.classMap(stateLayerClasses)}"></div>`)}
				${when(this.busy, () => html`<schmancy-spinner aria-hidden="true" .size=${BUSY_SPINNER_SIZE[this.size]}></schmancy-spinner>`)}
				${
					this.text
						? html`<slot></slot>`
						: html`
								${when(
									!this.busy,
									() => html`<schmancy-icon class=${iconSizeClass}>${this.icon || this.capturedIcon}</schmancy-icon>`,
								)}
							`
				}
			</button>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-icon-button': SchmnacyIconButton
	}
}
